/**
 * Cart Update Handler using Shopify Cart API and Section Rendering API
 * Handles real-time updates for quantity changes, cart attributes, and line item properties
 */

class CartUpdateHandler {
  constructor() {
    this.init();
    this.debounceTimeout = null;
    this.updateInProgress = false;
    document.addEventListener("rebuy.loaded", (event) => {
      setTimeout(() => {
        this.updateProgressbar();
      }, 1000);
    });

    this.bindRebuyAdd();
  }

  init() {
    this.bindQuantityControls();
    this.bindAttributeFields();
    this.bindRemoveButtons();
    this.setupLoadingStates();
  }

  /**
   * Bind quantity control events (plus, minus, direct input)
   */
  bindQuantityControls() {
    // Plus/minus buttons
    document.querySelectorAll(".js-qty__adjust").forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        this.handleQuantityChange(button);
      });
    });

    // Direct input changes with debounce
    document.querySelectorAll(".js-qty__num").forEach((input) => {
      input.addEventListener("input", (e) => {
        this.debounceQuantityUpdate(input);
      });

      // Handle enter key
      input.addEventListener("keypress", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          this.updateQuantityFromInput(input);
        }
      });
    });
  }

  /**
   * Bind cart attribute fields (gift notes, special instructions)
   */
  bindAttributeFields() {
    // Gift note textarea
    const giftNoteField = document.querySelector("#CartGiftNote");
    if (giftNoteField) {
      giftNoteField.addEventListener("blur", () => {
        this.updateCartAttributes();
      });
    }

    // Special instructions textarea
    const instructionsField = document.querySelector(
      "#CartSpecialInstructions"
    );
    if (instructionsField) {
      instructionsField.addEventListener("blur", () => {
        this.updateCartNote(instructionsField.value);
      });
    }

    // No invoice gift checkbox
    const noInvoiceCheckbox = document.querySelector("#NoInvoiceGift");
    if (noInvoiceCheckbox) {
      noInvoiceCheckbox.addEventListener("change", () => {
        this.updateCartAttributes();
      });
    }
  }

  /**
   * Bind remove button events
   */
  bindRemoveButtons() {
    document.querySelectorAll(".cart__remove").forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        this.removeLineItem(link);
      });
    });
  }

  /**
   * Bind Rebuy Add
   */

  bindRebuyAdd() {
    document.addEventListener(
      "rebuy.add",
      function (event) {
        setTimeout(() => {
          this.refreshCartSection();
        }, 1000);
      }.bind(this)
    );
  }

  /**
   * Handle quantity button clicks (plus/minus)
   */
  handleQuantityChange(button) {
    if (this.updateInProgress) return;

    const qtyWrapper = button.closest(".js-qty");
    const input = qtyWrapper.querySelector(".js-qty__num");
    const currentQty = parseInt(input.value) || 0;
    const isPlus = button.classList.contains("js-qty__adjust--plus");
    const isMinus = button.classList.contains("js-qty__adjust--minus");

    let newQty = currentQty;

    if (isPlus) {
      newQty = currentQty + 1;
    } else if (isMinus && currentQty > 0) {
      newQty = currentQty - 1;
    }

    if (newQty !== currentQty && newQty >= 0) {
      this.updateLineItemQuantity(input, newQty);
    }
  }

  /**
   * Debounced quantity update for direct input
   */
  debounceQuantityUpdate(input) {
    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(() => {
      this.updateQuantityFromInput(input);
    }, 500);
  }

  /**
   * Update quantity from direct input
   */
  updateQuantityFromInput(input) {
    if (this.updateInProgress) return;

    const newQty = parseInt(input.value) || 0;
    if (newQty >= 0) {
      this.updateLineItemQuantity(input, newQty);
    }
  }

  /**
   * Update line item quantity via Cart API
   */
  async updateLineItemQuantity(input, quantity) {
    const lineIndex = this.getLineIndex(input);
    if (lineIndex === null) return;

    this.setLoadingState(input, true);

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          line: lineIndex,
          quantity: quantity,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedCart = await response.json();
      await this.refreshCartSection();

      // Trigger custom event for other scripts
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart: updatedCart },
        })
      );
    } catch (error) {
      console.error("Error updating quantity:", error);
      // Revert input value
      input.value = input.getAttribute("data-original-value") || "1";
    } finally {
      this.setLoadingState(input, false);
    }
  }

  async addGift() {
    const progressBar = Rebuy.SmartCart.getFirstComponentByType(
      "tiered_progress_bar"
    );

    const progressBarTragetProduct =
      progressBar.settings.bars[0].tiers.at(-1).products[0];

    const giftFormData = new FormData();
    giftFormData.append("id", progressBarTragetProduct.variant_ids[0]);
    giftFormData.append("quantity", "1");
    giftFormData.append("properties[_source]", "Rebuy");
    giftFormData.append(
      "properties[_attribution]",
      "Rebuy Tiered Progress Bar"
    );
    giftFormData.append(
      "properties[_tier]",
      progressBar.settings.bars[0].tiers.at(-1).id
    );

    await fetch("/cart/add.js", {
      method: "POST",
      body: giftFormData,
    });
  }

  /**
   * Remove line item from cart
   */
  async removeLineItem(removeLink) {
    if (this.updateInProgress) return;

    const lineIndex = this.getLineIndexFromRemoveLink(removeLink);
    if (lineIndex === null) return;

    this.setLoadingState(removeLink, true);

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          line: lineIndex,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedCart = await response.json();
      await this.refreshCartSection();

      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart: updatedCart },
        })
      );
    } catch (error) {
      console.error("Error removing item:", error);
    } finally {
      this.setLoadingState(removeLink, false);
    }
  }

  /**
   * Update cart attributes (gift note, no invoice, etc.)
   */
  async updateCartAttributes() {
    if (this.updateInProgress) return;

    const giftNote = document.querySelector("#CartGiftNote")?.value || "";
    const noInvoiceGift = document.querySelector("#NoInvoiceGift")?.checked
      ? "🔴on"
      : "";

    const attributes = {
      gift_note: giftNote,
      "no-invoice-gift": noInvoiceGift,
    };

    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          attributes: attributes,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedCart = await response.json();

      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart: updatedCart },
        })
      );
    } catch (error) {
      console.error("Error updating cart attributes:", error);
    }
  }

  async updateProgressbar() {
    const titleElement = document.querySelector(
      ".rebuy-cart__progress-bar-prompt"
    );
    const progressBarContainerElement = document.querySelector(
      ".rebuy-cart__progress-bar-meter-fill"
    );
    if (!progressBarContainerElement) return;
    const progressBar = Rebuy.SmartCart.getFirstComponentByType(
      "tiered_progress_bar"
    );
    const cartTotal =
      Rebuy.SmartCart.progressBarSubtotal() / 100 / Shopify.currency.rate;
    const progressBarTragetProduct =
      progressBar.settings.bars[0].tiers.at(-1).products[0];
    const progressBarTragetTotal = progressBarTragetProduct.minimum;

    let progressPercentage = 36;
    if (cartTotal >= progressBarTragetTotal) {
      progressPercentage = 100;

      const hasGift = Rebuy.SmartCart.items().some((item) =>
        item.properties?._attribution?.includes("Rebuy Tiered Progress Bar")
      );

      titleElement.innerHTML =
        "You have unlocked a free Mylar Thermal Space Blanket | FIR Sauna Wrap!";

      if (!hasGift) {
        await this.addGift();
        await this.refreshCartSection();
      }
    } else if (cartTotal > 0 && cartTotal < progressBarTragetTotal) {
      const amountLeft =
        progressBarTragetProduct.minimum * Shopify.currency.rate * 100 -
        Rebuy.SmartCart.progressBarSubtotal();

      titleElement.innerHTML = `You are ${wetheme.formatMoney(
        amountLeft
      )} away from a free Mylar Thermal Space Blanket | FIR Sauna Wrap!`;
      progressPercentage =
        36 + (cartTotal / progressBarTragetTotal) * (64 - 36);
    } else {
      titleElement.innerHTML = `Add items worth ${wetheme.formatMoney(
        progressBarTragetProduct.minimum * Shopify.currency.rate * 100
      )} to unlock a free Mylar Thermal Space Blanket | FIR Sauna Wrap!`;
    }
    progressBarContainerElement.style.width = `${progressPercentage}%`;
  }

  /**
   * Update cart note (special instructions)
   */
  async updateCartNote(note) {
    if (this.updateInProgress) return;

    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          note: note,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedCart = await response.json();

      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart: updatedCart },
        })
      );
    } catch (error) {
      console.error("Error updating cart note:", error);
    }
  }

  /**
   * Refresh cart section using Section Rendering API
   */
  async refreshCartSection() {
    try {
      const response = await fetch(
        `${window.location.pathname}?section_id=template--cart`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const newDoc = parser.parseFromString(html, "text/html");

      // Update cart table
      const currentTable = document.querySelector(".cart-table");
      const newTable = newDoc.querySelector(".cart-table");
      if (currentTable && newTable) {
        currentTable.replaceWith(newTable);
      }

      // Update subtotal section
      const currentSubtotal = document.querySelector(".cart__subtotal");
      const newSubtotal = newDoc.querySelector(".cart__subtotal");
      if (currentSubtotal && newSubtotal) {
        currentSubtotal.replaceWith(newSubtotal);
      }

      // Update discount sections
      const currentDiscounts = document.querySelector(
        ".cart--order-discount-wrapper"
      );
      const newDiscounts = newDoc.querySelector(
        ".cart--order-discount-wrapper"
      );
      if (newDiscounts) {
        if (currentDiscounts) {
          currentDiscounts.replaceWith(newDiscounts);
        } else {
          // Insert new discounts if they don't exist
          const subtotalElement = document.querySelector(
            ".limoniapps-discountninja-cart-subtotal"
          );
          if (subtotalElement) {
            subtotalElement.insertAdjacentHTML(
              "beforebegin",
              newDiscounts.outerHTML
            );
          }
        }
      } else if (currentDiscounts) {
        currentDiscounts.remove();
      }

      // Re-bind events after DOM update

      this.init();

      setTimeout(() => {
        const cartToral = document.querySelector(
          ".limoniapps-discountninja-cart-subtotal .cart__subtotal"
        );
        if (cartToral) {
          cartToral.innerHTML = wetheme.formatMoney(
            Rebuy.SmartCart.progressBarSubtotal()
          );
        }
        this.updateProgressbar();
      }, 500);
    } catch (error) {
      console.error("Error refreshing cart section:", error);
      // Fallback: reload the page
      window.location.reload();
    }
  }

  /**
   * Get line index from quantity input
   */
  getLineIndex(input) {
    const id = input.getAttribute("id");
    if (id && id.startsWith("updates_")) {
      // Get the line index from the row
      const row = input.closest("tr");
      const allRows = Array.from(
        document.querySelectorAll(".cart__row.table__section")
      );
      return allRows.indexOf(row) + 1; // Shopify uses 1-based indexing
    }
    return null;
  }

  /**
   * Get line index from remove link
   */
  getLineIndexFromRemoveLink(link) {
    const href = link.getAttribute("href");
    const match = href.match(/line=(\d+)/);
    return match ? parseInt(match[1]) : null;
  }

  /**
   * Setup loading states and original values
   */
  setupLoadingStates() {
    // Store original values
    document.querySelectorAll(".js-qty__num").forEach((input) => {
      input.setAttribute("data-original-value", input.value);
    });
  }

  /**
   * Set loading state for an element
   */
  setLoadingState(element, isLoading) {
    this.updateInProgress = isLoading;

    if (isLoading) {
      element.style.opacity = "0.6";
      element.style.pointerEvents = "none";
    } else {
      element.style.opacity = "1";
      element.style.pointerEvents = "auto";
      if (element.tagName === "INPUT") {
        element.style.background = "";
      }
    }
  }
}

// Initialize cart update handler when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  new CartUpdateHandler();
});

// Handle dynamic content loading (if using AJAX)
document.addEventListener("shopify:section:load", () => {
  new CartUpdateHandler();
});
